/**
 * BeamDesignPage — rectangular reinforced concrete beam design (E.060 + ACI 318).
 *
 * Layout: left rail (inputs)  ·  canvas (section + elevation diagrams)  ·  right rail (results).
 */

import { useMemo, useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Activity, Info, X as XIcon } from 'lucide-react';
import { useBeamStore } from '@/features/beams/store';
import { useBeamAnalysis } from '@/features/beams/useBeamAnalysis';
import { useExportBeam } from '@/features/beams/useExport';
import { Beam3DView } from '@/features/beams/Beam3DView';
import { ExportCostHint } from '@/features/billing/ExportCostHint';
import { DesignToolbar } from '@/features/designs/DesignToolbar';
import { SaveDesignDialog } from '@/features/designs/SaveDesignDialog';
import { useDesign } from '@/features/designs/useDesigns';
import type { BeamAnalyzeRequest, BeamPrefill, BeamType, FlexionSection, PointLoad } from '@/features/beams/types';

export function BeamDesignPage() {
  const request = useBeamStore((s) => s.request);
  const setSection = useBeamStore((s) => s.setSection);
  const setMaterials = useBeamStore((s) => s.setMaterials);
  const setLoads = useBeamStore((s) => s.setLoads);
  const setReinforcement = useBeamStore((s) => s.setReinforcement);
  const setType = useBeamStore((s) => s.setType);
  const loadRequest = useBeamStore((s) => s.loadRequest);
  const beamType = request.section.type;
  const typeInfo = BEAM_TYPE_INFO[beamType];

  // URL hydration: /app/beams/{id} loads a saved design into the store.
  const params = useParams<{ id?: string }>();
  const designId =
    params.id && params.id !== 'new' && /^\d+$/.test(params.id) ? Number(params.id) : null;
  const designQuery = useDesign(designId);
  useEffect(() => {
    if (designQuery.data) {
      loadRequest(designQuery.data.payload as unknown as BeamAnalyzeRequest);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [designQuery.data?.id]);

  // ---------------- Prefill desde el módulo de Análisis ----------------
  // Cuando el usuario llega desde `/app/analysis/beams/...` vía el botón
  // "Diseñar viga →", traemos en `location.state.prefill` la geometría,
  // los materiales y las cargas (Mu, Vu) ya calculadas. Hidratamos el
  // store y mostramos un banner azul que el usuario puede limpiar para
  // volver al modo manual.
  const location = useLocation();
  const navigate = useNavigate();
  const initialPrefill = (location.state as { prefill?: BeamPrefill } | null)?.prefill ?? null;
  const [prefillActive, setPrefillActive] = useState<BeamPrefill | null>(initialPrefill);

  useEffect(() => {
    if (initialPrefill) {
      // Construir un BeamAnalyzeRequest a partir del prefill.
      const next: BeamAnalyzeRequest = {
        section: {
          type: (initialPrefill.section.type as BeamType) ?? 'rectangular',
          b: initialPrefill.section.b,
          h: initialPrefill.section.h,
          L_span: initialPrefill.section.L_span,
        },
        materials: {
          fc: initialPrefill.materials.fc,
          fy: initialPrefill.materials.fy,
          cover: initialPrefill.materials.cover,
          TMA: initialPrefill.materials.TMA,
        },
        loads: {
          Mu_neg_left: initialPrefill.loads.Mu_neg_left,
          Mu_pos_mid: initialPrefill.loads.Mu_pos_mid,
          Mu_neg_right: initialPrefill.loads.Mu_neg_right,
          Vu_left: initialPrefill.loads.Vu_left,
          Vu_right: initialPrefill.loads.Vu_right,
        },
        reinforcement: { stirrup_diam: '3/8', stirrup_legs: 2 },
      };
      loadRequest(next);
    }
    // Solo al montar — el banner persiste hasta que el usuario lo limpia.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearPrefill = () => {
    setPrefillActive(null);
    // Limpiar el state del location para que no reaparezca en navegaciones
    navigate('.', { replace: true, state: null });
  };
  // ---------------------------------------------------------------------

  const [saveOpen, setSaveOpen] = useState(false);

  const { data, isFetching, isError, error } = useBeamAnalysis(request);
  const exporter = useExportBeam();
  const [elevationView, setElevationView] = useState<'2D' | '3D'>('2D');
  const [mode, setMode] = useState<'design' | 'analysis'>('design');
  const appliedLoads = useBeamStore((s) => s.appliedLoads);
  const addPointLoad = useBeamStore((s) => s.addPointLoad);
  const updatePointLoad = useBeamStore((s) => s.updatePointLoad);
  const removePointLoad = useBeamStore((s) => s.removePointLoad);

  // Live statics on the applied loads (assuming a simply-supported beam of
  // length L). Returns the reactions, max positive moment, and the location
  // where Mu_max occurs — used to suggest design values to the user.
  const stats = useMemo(() => {
    return simpleBeamStatics(appliedLoads, request.section.L_span);
  }, [appliedLoads, request.section.L_span]);

  // Hidden 3D viewer dedicated to PDF snapshot capture. Always mounted so
  // `toDataURL()` returns a valid PNG even when the user is in 2D view.
  const snapshotContainerRef = useRef<HTMLDivElement | null>(null);

  /** Captures the hidden 3D canvas as a base64 PNG data URI (or null on failure). */
  const captureSnapshot = (): string | null => {
    const el = snapshotContainerRef.current?.querySelector('canvas') as HTMLCanvasElement | null;
    if (!el) return null;
    try {
      return el.toDataURL('image/png');
    } catch (e) {
      console.warn('[beam-3d] toDataURL failed:', e);
      return null;
    }
  };

  return (
    <div className="flex h-full flex-col">
      <header
        className="flex h-14 shrink-0 items-center gap-6 border-b px-6"
        style={{ background: 'var(--surface-1)', borderColor: 'var(--border-subtle)' }}
      >
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>
            Software E.060
          </div>
          <div className="text-sm font-semibold leading-none" style={{ color: 'var(--text-primary)' }}>
            Diseño de vigas rectangulares
          </div>
        </div>
        {/* Mode toggle (centered): Diseño vs Análisis */}
        <div className="ml-6 flex overflow-hidden rounded-[var(--radius-sm)] border" style={{ borderColor: 'var(--border-default)' }}>
          {(['design', 'analysis'] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              className="px-4 py-1.5 text-[11px] font-semibold"
              style={{
                background: mode === m ? 'var(--accent)' : 'transparent',
                color: mode === m ? 'var(--surface-base)' : 'var(--text-secondary)',
              }}
            >
              {m === 'design' ? 'Diseño de sección' : 'Análisis de envolventes'}
            </button>
          ))}
        </div>

        {/* Right side intentionally empty — DesignToolbar handles status + exports. */}
      </header>

      {/* Banner cuando vinieron los Mu/Vu desde el módulo de Análisis. */}
      {prefillActive && (
        <div
          className="flex flex-wrap items-center justify-between gap-3 border-b px-6 py-2.5 text-[12.5px]"
          style={{
            background: 'rgba(96,165,250,0.10)',
            borderColor: 'rgba(96,165,250,0.35)',
            color: 'var(--text-secondary)',
          }}
        >
          <div className="flex items-center gap-2">
            <Info size={14} strokeWidth={1.8} style={{ color: '#60a5fa' }} />
            <span>
              <strong style={{ color: 'var(--text-primary)' }}>Valores cargados desde análisis estructural.</strong>{' '}
              Puedes ajustarlos antes de calcular el refuerzo.
            </span>
          </div>
          <div className="flex items-center gap-2">
            {prefillActive.analysisId && (
              <Link
                to={`/app/analysis/beams/${prefillActive.analysisId}`}
                className="inline-flex items-center gap-1 rounded-[var(--radius-sm)] border px-2 py-1 text-[11.5px] no-underline transition-colors hover:bg-[var(--surface-2)]"
                style={{ borderColor: 'var(--border-default)', color: 'var(--text-primary)' }}
              >
                <Activity size={12} strokeWidth={1.8} /> Ver análisis
              </Link>
            )}
            <button
              type="button"
              onClick={clearPrefill}
              className="inline-flex items-center gap-1 rounded-[var(--radius-sm)] border px-2 py-1 text-[11.5px] transition-colors hover:bg-[var(--surface-2)]"
              style={{ borderColor: 'var(--border-default)', color: 'var(--text-secondary)' }}
            >
              <XIcon size={11} strokeWidth={1.8} /> Limpiar
            </button>
          </div>
        </div>
      )}

      {/* Per-beam-type independence: switching the type tab while on
          /app/beams/:id flips Save back to "create new" because the
          design we loaded was for a different beam type. */}
      {(() => {
        const matchingDesign =
          designQuery.data && designQuery.data.subtype === beamType
            ? designQuery.data
            : null;
        return (
          <>
            <DesignToolbar
              status={isError ? 'error' : isFetching ? 'busy' : 'ready'}
              statusLabel={isError ? `Error: ${String(error)}` : undefined}
              onExportPdf={() => exporter.trigger('pdf', request, appliedLoads, captureSnapshot())}
              onExportXlsx={() => exporter.trigger('xlsx', request, appliedLoads)}
              busyExport={exporter.busy}
              onSave={() => setSaveOpen(true)}
              isUpdate={!!matchingDesign}
            />
            <SaveDesignDialog
              open={saveOpen}
              onOpenChange={setSaveOpen}
              module="beams"
              subtype={beamType}
              payload={request as unknown as Record<string, unknown>}
              existing={matchingDesign}
            />
          </>
        );
      })()}

      <div className="flex flex-1 overflow-hidden">
        {/* ============== LEFT RAIL — INPUTS ============== */}
        <aside
          className="flex w-[280px] shrink-0 flex-col gap-3 overflow-y-auto border-r p-3 text-[11px]"
          style={{ background: 'var(--surface-1)', borderColor: 'var(--border-default)' }}
        >
          <Group title="Tipo de viga">
            <BeamTypePicker value={beamType} onChange={setType} />
            <div
              className="mt-2 rounded-[var(--radius-sm)] border px-2 py-1.5"
              style={{
                background: 'rgba(245,158,11,0.06)',
                borderColor: 'rgba(245,158,11,0.30)',
                color: 'var(--text-secondary)',
                lineHeight: 1.45,
                fontSize: '10.5px',
              }}
            >
              <div style={{ color: 'var(--accent)', fontWeight: 700, marginBottom: 2 }}>
                {typeInfo.title}
              </div>
              <div>{typeInfo.description}</div>
              {typeInfo.note && (
                <div className="mt-1 text-[9.5px]" style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>
                  {typeInfo.note}
                </div>
              )}
            </div>
          </Group>

          <Group title="Materiales">
            <NumberRow label="f'c (kgf/cm²)" value={request.materials.fc} onChange={(v) => setMaterials({ fc: v })} />
            <NumberRow label="fy (kgf/cm²)" value={request.materials.fy} onChange={(v) => setMaterials({ fy: v })} />
            <NumberRow label="Recubrimiento (cm)" value={request.materials.cover} onChange={(v) => setMaterials({ cover: v })} step={0.5} />
            <NumberRow label="TMA (cm)" value={request.materials.TMA} onChange={(v) => setMaterials({ TMA: v })} step={0.5} />
          </Group>

          <Group title="Geometria">
            <NumberRow label="b (cm) — ancho" value={request.section.b} onChange={(v) => setSection({ b: v })} />
            <NumberRow label="h (cm) — peralte" value={request.section.h} onChange={(v) => setSection({ h: v })} />
            <NumberRow label="L (cm) — luz libre" value={request.section.L_span} onChange={(v) => setSection({ L_span: v })} step={10} />
          </Group>

          <Group title="Cargas factoradas">
            <NumberRow label="Mu− izq. (kgf·cm)" value={request.loads.Mu_neg_left} onChange={(v) => setLoads({ Mu_neg_left: v })} step={100_000} />
            <NumberRow label="Mu+ centro (kgf·cm)" value={request.loads.Mu_pos_mid} onChange={(v) => setLoads({ Mu_pos_mid: v })} step={100_000} />
            <NumberRow label="Mu− der. (kgf·cm)" value={request.loads.Mu_neg_right} onChange={(v) => setLoads({ Mu_neg_right: v })} step={100_000} />
            <NumberRow label="Vu izq. (kgf)" value={request.loads.Vu_left} onChange={(v) => setLoads({ Vu_left: v })} step={1000} />
            <NumberRow label="Vu der. (kgf)" value={request.loads.Vu_right} onChange={(v) => setLoads({ Vu_right: v })} step={1000} />
          </Group>

          {/* CARGAS PUNTUALES — visualization on the beam + statics calculator */}
          <Group title="Cargas puntuales aplicadas">
            {appliedLoads.length === 0 && (
              <div className="text-[10px]" style={{ color: 'var(--text-muted)', lineHeight: 1.45 }}>
                Agrega cargas puntuales para visualizarlas en la elevación y calcular Mu_max y Vu automáticamente (apoyo simple).
              </div>
            )}
            {appliedLoads.map((load, i) => (
              <div key={load.id} className="rounded-[var(--radius-sm)] border p-1.5"
                style={{ background: 'var(--surface-2)', borderColor: 'var(--border-default)', marginTop: i === 0 ? 0 : 4 }}>
                <div className="mb-1 flex items-center justify-between">
                  <span className="text-[9.5px] font-semibold uppercase tracking-[0.10em]" style={{ color: 'var(--text-muted)' }}>
                    Carga #{i + 1}
                  </span>
                  <button
                    type="button"
                    onClick={() => removePointLoad(load.id)}
                    className="text-[10px] hover:opacity-80"
                    style={{ color: 'var(--danger)' }}
                    title="Eliminar"
                  >
                    × Eliminar
                  </button>
                </div>
                <NumberRow
                  label="P (kgf)"
                  value={load.P_kgf}
                  onChange={(v) => updatePointLoad(load.id, { P_kgf: v })}
                  step={500}
                />
                <NumberRow
                  label="x desde apoyo izq. (cm)"
                  value={load.x_cm}
                  onChange={(v) => updatePointLoad(load.id, { x_cm: Math.max(0, Math.min(request.section.L_span, v)) })}
                  step={10}
                />
              </div>
            ))}
            <button
              type="button"
              onClick={() =>
                addPointLoad({
                  id: `pl-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
                  P_kgf: 5000,
                  x_cm: Math.round(request.section.L_span / 2),
                })
              }
              className="mt-2 w-full rounded-[var(--radius-sm)] border px-2 py-1.5 text-[10.5px] font-semibold transition-colors hover:bg-[var(--surface-2)]"
              style={{ borderColor: 'var(--accent)', color: 'var(--accent)', background: 'transparent' }}
            >
              + Agregar carga puntual
            </button>

            {appliedLoads.length > 0 && (
              <div className="mt-2 rounded-[var(--radius-sm)] border p-2"
                style={{ background: 'rgba(59,130,246,0.06)', borderColor: 'rgba(59,130,246,0.30)' }}>
                <div className="mb-1 text-[9px] font-semibold uppercase tracking-[0.14em]" style={{ color: 'var(--brand-alt)' }}>
                  Estática apoyo simple
                </div>
                <KV label="R izq. = Vu izq." value={`${(stats.RA / 1000).toFixed(2)} tonf`} />
                <KV label="R der. = Vu der." value={`${(stats.RB / 1000).toFixed(2)} tonf`} />
                <KV label="Mu+ máx." value={`${(stats.Mmax / 1e5).toFixed(2)} tonf·m (en x=${stats.xMmax.toFixed(0)} cm)`} />
                <button
                  type="button"
                  onClick={() => {
                    setLoads({
                      Vu_left: stats.RA,
                      Vu_right: stats.RB,
                      Mu_pos_mid: stats.Mmax,
                    });
                  }}
                  className="mt-1.5 w-full rounded-[var(--radius-sm)] border px-2 py-1 text-[10px] font-semibold transition-colors hover:bg-[var(--surface-2)]"
                  style={{ borderColor: 'var(--brand-alt)', color: 'var(--brand-alt)' }}
                  title="Reemplaza Vu_izq, Vu_der y Mu+ centro con los valores calculados"
                >
                  ↑ Aplicar a Mu+ / Vu del diseño
                </button>
              </div>
            )}
          </Group>

          <Group title="Acero longitudinal">
            <div className="mb-2 flex overflow-hidden rounded-[var(--radius-sm)] border" style={{ borderColor: 'var(--border-default)' }}>
              {(['auto', 'manual'] as const).map((mode) => {
                const isManual = (request.reinforcement.n_pos != null) || (request.reinforcement.n_neg != null);
                const active = (mode === 'manual') === isManual;
                return (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => {
                      if (mode === 'auto') {
                        setReinforcement({ n_pos: null, n_neg: null, diam_pos: null, diam_neg: null });
                      } else {
                        // Switching to manual: seed with reasonable defaults
                        setReinforcement({
                          n_pos: request.reinforcement.n_pos ?? 4,
                          n_neg: request.reinforcement.n_neg ?? 3,
                          diam_pos: request.reinforcement.diam_pos ?? '5/8',
                          diam_neg: request.reinforcement.diam_neg ?? '3/4',
                        });
                      }
                    }}
                    className="flex-1 px-2 py-1.5 text-[10px] font-semibold uppercase tracking-[0.1em]"
                    style={{
                      background: active ? 'var(--accent)' : 'transparent',
                      color: active ? 'var(--surface-base)' : 'var(--text-secondary)',
                    }}
                  >
                    {mode === 'auto' ? '⚙ Auto' : '✎ Manual'}
                  </button>
                );
              })}
            </div>
            {(request.reinforcement.n_pos != null) || (request.reinforcement.n_neg != null) ? (
              <>
                <div className="mb-1 mt-2 text-[9px] uppercase tracking-[0.14em]" style={{ color: 'var(--text-muted)' }}>Tracción inferior (Mu+)</div>
                <NumberRow
                  label="N° barras"
                  value={request.reinforcement.n_pos ?? 4}
                  onChange={(v) => setReinforcement({ n_pos: Math.max(2, Math.min(12, Math.round(v))) })}
                  step={1}
                />
                <SelectRow
                  label="Diametro"
                  value={request.reinforcement.diam_pos ?? '5/8'}
                  options={['3/8', '1/2', '5/8', '3/4', '1', '1 3/8']}
                  onChange={(v) => setReinforcement({ diam_pos: v as any })}
                />
                <div className="mb-1 mt-3 text-[9px] uppercase tracking-[0.14em]" style={{ color: 'var(--text-muted)' }}>Tracción superior (Mu−)</div>
                <NumberRow
                  label="N° barras"
                  value={request.reinforcement.n_neg ?? 3}
                  onChange={(v) => setReinforcement({ n_neg: Math.max(2, Math.min(12, Math.round(v))) })}
                  step={1}
                />
                <SelectRow
                  label="Diametro"
                  value={request.reinforcement.diam_neg ?? '3/4'}
                  options={['3/8', '1/2', '5/8', '3/4', '1', '1 3/8']}
                  onChange={(v) => setReinforcement({ diam_neg: v as any })}
                />
              </>
            ) : (
              <div className="text-[10px]" style={{ color: 'var(--text-muted)', lineHeight: 1.45 }}>
                El sistema elige automáticamente la menor cantidad de barras de un diametro común que cumpla As_req y entre cómoda en <span style={{ fontFamily: 'monospace' }}>b</span>.
              </div>
            )}
          </Group>

          <Group title="Estribos">
            <SelectRow
              label="Diametro"
              value={request.reinforcement.stirrup_diam}
              options={['3/8', '1/2', '5/8']}
              onChange={(v) => setReinforcement({ stirrup_diam: v as any })}
            />
            <NumberRow label="Ramales" value={request.reinforcement.stirrup_legs} onChange={(v) => setReinforcement({ stirrup_legs: Math.round(v) })} step={1} />
          </Group>
        </aside>

        {/* ============== CENTER — DIAGRAMS ============== */}
        <main className="flex-1 overflow-auto p-4" style={{ background: 'var(--surface-canvas)' }}>
          <div className="flex flex-col gap-4">
            {data && mode === 'analysis' && (
              <>
                <DiagramCard title="Envolvente de momento flector M(x)">
                  <MomentEnvelopeDiagram
                    L={request.section.L_span}
                    Mu_neg_left={request.loads.Mu_neg_left}
                    Mu_pos_mid={request.loads.Mu_pos_mid}
                    Mu_neg_right={request.loads.Mu_neg_right}
                    phi_Mn_pos={data.flexion_pos_mid.phi_Mn}
                    phi_Mn_neg_left={data.flexion_neg_left.phi_Mn}
                    phi_Mn_neg_right={data.flexion_neg_right.phi_Mn}
                    h={request.section.h}
                  />
                </DiagramCard>
                <DiagramCard title="Envolvente de fuerza cortante V(x)">
                  <ShearEnvelopeDiagram
                    L={request.section.L_span}
                    Vu_left={request.loads.Vu_left}
                    Vu_right={request.loads.Vu_right}
                    zones={data.shear.zones}
                    Vc={data.shear.Vc}
                    Av_one_leg={data.shear.Av_one_leg}
                    n_legs={data.shear.n_legs}
                    fy={request.materials.fy}
                    d={data.outputs.d_effective}
                  />
                </DiagramCard>
              </>
            )}
            {data && mode === 'design' && (
              <>
                <DiagramCard title="Sección transversal">
                  <BeamSectionDiagram
                    b={request.section.b}
                    h={request.section.h}
                    cover={request.materials.cover}
                    barsTopTension={data.flexion_neg_left.bars}
                    barsTopCompression={data.flexion_pos_mid.bars_compression}
                    barsBotTension={data.flexion_pos_mid.bars}
                    barsBotCompression={data.flexion_neg_left.bars_compression}
                    beamType={beamType}
                  />
                </DiagramCard>

                <DiagramCard
                  title="Elevacion - distribucion del estribado y aceros"
                  toolbar={
                    <div className="flex overflow-hidden rounded-[var(--radius-sm)] border" style={{ borderColor: 'var(--border-default)' }}>
                      {(['2D', '3D'] as const).map((mode) => (
                        <button
                          key={mode}
                          type="button"
                          onClick={() => setElevationView(mode)}
                          className="h-7 px-3 text-[10px] font-semibold tracking-[0.12em]"
                          style={{
                            background: elevationView === mode ? 'var(--accent)' : 'transparent',
                            color: elevationView === mode ? 'var(--surface-base)' : 'var(--text-secondary)',
                          }}
                        >
                          {mode}
                        </button>
                      ))}
                    </div>
                  }
                >
                  {elevationView === '2D' ? (
                    <BeamElevationDiagram
                      L={request.section.L_span}
                      h={request.section.h}
                      zones={data.shear.zones}
                      posBars={data.flexion_pos_mid.bars.label}
                      negBars={data.flexion_neg_left.bars.label}
                      inverted={beamType === 'invertida'}
                      appliedLoads={appliedLoads}
                      onLoadDrag={(id, x_cm) => updatePointLoad(id, { x_cm })}
                    />
                  ) : (
                    <div style={{ width: 900, height: 420 }}>
                      <Beam3DView
                        L={request.section.L_span}
                        b={request.section.b}
                        h={request.section.h}
                        cover={request.materials.cover}
                        d_stirrup={diamInchToCm(request.reinforcement.stirrup_diam)}
                        d_bar_top={diamInchToCm(data.flexion_neg_left.bars.diameters[0] ?? '5/8')}
                        d_bar_bot={diamInchToCm(data.flexion_pos_mid.bars.diameters[0] ?? '5/8')}
                        n_bars_top={data.flexion_neg_left.bars.n_bars}
                        n_bars_bot={data.flexion_pos_mid.bars.n_bars}
                        n_bars_top_comp={data.flexion_pos_mid.bars_compression?.n_bars ?? 0}
                        n_bars_bot_comp={Math.max(
                          data.flexion_neg_left.bars_compression?.n_bars ?? 0,
                          data.flexion_neg_right.bars_compression?.n_bars ?? 0,
                        )}
                        zones={data.shear.zones}
                        inverted={beamType === 'invertida'}
                        beamType={beamType}
                        appliedLoads={appliedLoads}
                      />
                    </div>
                  )}
                </DiagramCard>
              </>
            )}
          </div>
        </main>

        {/* ============== RIGHT RAIL — RESULTS ============== */}
        <aside
          className="flex w-[340px] shrink-0 flex-col gap-3 overflow-y-auto border-l p-3 text-[11px]"
          style={{ background: 'var(--surface-1)', borderColor: 'var(--border-default)' }}
        >
          {data && (
            <>
              <ResultCard title="Resumen de la sección">
                <KV label="Ag" value={`${data.outputs.Ag.toFixed(0)} cm²`} />
                <KV label="d efectivo" value={`${data.outputs.d_effective.toFixed(1)} cm`} />
                <KV label="Acero superior" value={data.outputs.bars_tag_top} mono />
                <KV label="Acero inferior" value={data.outputs.bars_tag_bot} mono />
              </ResultCard>

              <FlexionCard f={data.flexion_neg_left} />
              <FlexionCard f={data.flexion_pos_mid} />
              <FlexionCard f={data.flexion_neg_right} />

              <ResultCard title="Cortante">
                <KV label="Vu diseño" value={`${(data.shear.Vu_design / 1000).toFixed(2)} tonf`} />
                <KV label="φVc" value={`${(0.85 * data.shear.Vc / 1000).toFixed(2)} tonf`} />
                <KV label="Vs requerido" value={`${(data.shear.Vs_required / 1000).toFixed(2)} tonf`} />
                <KV label="s requerido" value={`${data.shear.s_required.toFixed(1)} cm`} />
                <div className="mt-2 space-y-1">
                  {data.shear.zones.map((z) => (
                    <div key={z.label} className="rounded px-2 py-1" style={{ background: 'var(--surface-2)' }}>
                      <div className="flex justify-between font-mono">
                        <span>{z.label}</span>
                        <span style={{ color: 'var(--accent)' }}>s = {z.spacing} cm × {z.n_stirrups}</span>
                      </div>
                      <div className="text-[9px]" style={{ color: 'var(--text-muted)' }}>{z.rule}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-1 text-[10px]" style={{ color: data.shear.ok ? 'var(--success)' : 'var(--danger)' }}>
                  {data.shear.governs}
                </div>
              </ResultCard>

              <ResultCard title="Verificaciones">
                {data.verifications.map((v) => (
                  <div key={v.id} className="flex items-center gap-2 py-0.5">
                    <span style={{ color: v.ok ? 'var(--success)' : 'var(--danger)', fontWeight: 700 }}>
                      {v.ok ? '✓' : '✗'}
                    </span>
                    <span className="flex-1 text-[10px]">{v.label}</span>
                  </div>
                ))}
              </ResultCard>
            </>
          )}
        </aside>
      </div>

      {/* HIDDEN 3D viewer — always mounted off-screen so that captureSnapshot()
          can call toDataURL() to embed the render into the exported PDF. */}
      {data && (
        <div
          ref={snapshotContainerRef}
          aria-hidden="true"
          style={{
            position: 'fixed',
            top: 0,
            left: -2400,
            width: 1100,
            height: 600,
            pointerEvents: 'none',
            opacity: 0,
          }}
        >
          <Beam3DView
            L={request.section.L_span}
            b={request.section.b}
            h={request.section.h}
            cover={request.materials.cover}
            d_stirrup={diamInchToCm(request.reinforcement.stirrup_diam)}
            d_bar_top={diamInchToCm(data.flexion_neg_left.bars.diameters[0] ?? '5/8')}
            d_bar_bot={diamInchToCm(data.flexion_pos_mid.bars.diameters[0] ?? '5/8')}
            n_bars_top={data.flexion_neg_left.bars.n_bars}
            n_bars_bot={data.flexion_pos_mid.bars.n_bars}
            n_bars_top_comp={data.flexion_pos_mid.bars_compression?.n_bars ?? 0}
            n_bars_bot_comp={Math.max(
              data.flexion_neg_left.bars_compression?.n_bars ?? 0,
              data.flexion_neg_right.bars_compression?.n_bars ?? 0,
            )}
            zones={data.shear.zones}
            inverted={beamType === 'invertida'}
            beamType={beamType}
            appliedLoads={appliedLoads}
          />
        </div>
      )}
    </div>
  );
}

/* =========================================================================
 *  HELPERS — inputs
 * ========================================================================= */

function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-[var(--radius-sm)] border p-2" style={{ borderColor: 'var(--border-default)' }}>
      <div className="mb-1.5 text-[9px] font-semibold uppercase tracking-[0.16em]" style={{ color: 'var(--text-muted)' }}>
        {title}
      </div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function NumberRow({
  label,
  value,
  onChange,
  step = 1,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  step?: number;
}) {
  return (
    <label className="flex items-center justify-between gap-2 py-0.5">
      <span className="text-[10.5px]" style={{ color: 'var(--text-secondary)' }}>{label}</span>
      <input
        type="number"
        value={value}
        step={step}
        onChange={(e) => {
          const v = parseFloat(e.target.value);
          if (!Number.isNaN(v)) onChange(v);
        }}
        className="w-[110px] rounded-[var(--radius-sm)] border px-2 py-1 text-right text-[11px] font-mono"
        style={{ background: 'var(--surface-2)', borderColor: 'var(--border-default)', color: 'var(--text-primary)' }}
      />
    </label>
  );
}

function SelectRow({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (v: string) => void;
}) {
  return (
    <label className="flex items-center justify-between gap-2 py-0.5">
      <span className="text-[10.5px]" style={{ color: 'var(--text-secondary)' }}>{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-[110px] rounded-[var(--radius-sm)] border px-2 py-1 text-right text-[11px] font-mono"
        style={{ background: 'var(--surface-2)', borderColor: 'var(--border-default)', color: 'var(--text-primary)' }}
      >
        {options.map((o) => (
          <option key={o} value={o}>{o}"</option>
        ))}
      </select>
    </label>
  );
}

/* =========================================================================
 *  HELPERS — results cards
 * ========================================================================= */

function ResultCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-[var(--radius-sm)] border p-2" style={{ background: 'var(--surface-2)', borderColor: 'var(--border-default)' }}>
      <div className="mb-1.5 text-[9px] font-semibold uppercase tracking-[0.16em]" style={{ color: 'var(--text-muted)' }}>
        {title}
      </div>
      <div>{children}</div>
    </div>
  );
}

function KV({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex justify-between py-0.5">
      <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{label}</span>
      <span className={mono ? 'font-mono text-[10.5px]' : 'text-[10.5px]'} style={{ color: 'var(--text-primary)' }}>
        {value}
      </span>
    </div>
  );
}

function FlexionCard({ f }: { f: FlexionSection }) {
  return (
    <ResultCard title={`Flexión — ${f.label}${f.is_doubly ? ' · 2× armado' : ''}`}>
      <KV label="Mu" value={`${(f.Mu / 1e5).toFixed(2)} tonf·m`} />
      <KV label="φMn" value={`${(f.phi_Mn / 1e5).toFixed(2)} tonf·m`} />
      <KV label="As tracción req." value={`${f.bars.As_required.toFixed(2)} cm²`} />
      <KV label="As tracción prov." value={`${f.bars.As_provided.toFixed(2)} cm²`} />
      <KV label="φ" value={f.phi.toFixed(3)} />
      <KV label="Barras (tracción)" value={f.bars.label} mono />
      {f.is_doubly && f.bars_compression && (
        <>
          <div className="my-1.5 h-px" style={{ background: 'var(--border-default)' }} />
          <KV label="As' compresión req." value={`${f.bars_compression.As_required.toFixed(2)} cm²`} />
          <KV label="As' compresión prov." value={`${f.bars_compression.As_provided.toFixed(2)} cm²`} />
          <KV label="d'" value={`${f.d_prime.toFixed(2)} cm`} />
          <KV label="Barras (compresión)" value={f.bars_compression.label} mono />
        </>
      )}
      <CuantiaBar rho={f.bars.rho} rho_min={f.bars.rho_min} rho_max={f.bars.rho_max} />
      <div className="mt-1 text-[9.5px]" style={{ color: f.ok ? 'var(--success)' : 'var(--danger)' }}>
        {f.ok ? '✓ ' : '✗ '}
        {f.governing}
      </div>
    </ResultCard>
  );
}

/**
 * CuantiaBar — visualization of ρ (longitudinal reinforcement ratio) showing
 * the position within the E.060 normative range:
 *   - 0% to ρ_min:   under-reinforced (RED, not safe)
 *   - ρ_min to 2.5%: óptimo / recomendado sismorresistente (GREEN)
 *   - 2.5% to ρ_max: alto (YELLOW)
 *   - > ρ_max:       sobre-reforzado (RED, prohibited)
 *
 * Width of the bar maps to [0, ρ_max + headroom]. A tick marks the actual ρ
 * with a status label underneath.
 */
function CuantiaBar({
  rho,
  rho_min,
  rho_max,
}: {
  rho: number;
  rho_min: number;
  rho_max: number;
}) {
  // Display range: from 0% to rho_max + a small headroom
  const rhoMaxDisplay = Math.max(rho_max + 0.005, rho + 0.005);
  const rhoMaxPct = rhoMaxDisplay * 100;
  const rhoPct = rho * 100;
  const rhoMinPct = rho_min * 100;
  const rhoRecPct = 2.5;   // sismorresistente recommended upper limit
  const rhoMaxNormPct = rho_max * 100;

  // Helper: percentage position (0..100) on the bar for a given rho %
  const pos = (p: number) => Math.max(0, Math.min(100, (p / rhoMaxPct) * 100));

  // Status calculation
  let statusText: string;
  let statusColor: string;
  if (rho < rho_min) {
    statusText = `Bajo cuantía mínima (${rhoMinPct.toFixed(2)}%) — under-reinforced`;
    statusColor = 'var(--danger)';
  } else if (rho <= rhoRecPct / 100) {
    statusText = `Óptimo — dentro del rango sismorresistente (${rhoMinPct.toFixed(2)}% – 2.5%)`;
    statusColor = 'var(--success)';
  } else if (rho <= rho_max) {
    statusText = `Alto — válido pero supera 2.5% recomendado`;
    statusColor = '#F59E0B'; // accent / warning
  } else {
    statusText = `Excede cuantía máxima (${rhoMaxNormPct.toFixed(1)}%) — re-dimensionar`;
    statusColor = 'var(--danger)';
  }

  const BAR_H = 8;

  return (
    <div className="mt-2">
      <div className="mb-0.5 flex items-baseline justify-between text-[10px]" style={{ color: 'var(--text-muted)' }}>
        <span>0%</span>
        <span style={{ color: 'var(--text-secondary)' }}>
          ρ = <span style={{ color: statusColor, fontFamily: 'monospace', fontWeight: 700 }}>{rhoPct.toFixed(2)}%</span>
        </span>
        <span>{rhoMaxPct.toFixed(0)}%</span>
      </div>
      <div className="relative w-full overflow-hidden rounded-[3px]"
        style={{ height: BAR_H, background: 'var(--surface-1)', border: '1px solid var(--border-default)' }}
      >
        {/* Red lower zone (0 → ρ_min) */}
        <div
          className="absolute top-0 left-0"
          style={{
            height: '100%',
            width: `${pos(rhoMinPct)}%`,
            background: 'rgba(220,38,38,0.32)',
          }}
        />
        {/* Green optimum zone (ρ_min → 2.5%) */}
        <div
          className="absolute top-0"
          style={{
            left: `${pos(rhoMinPct)}%`,
            width: `${pos(rhoRecPct) - pos(rhoMinPct)}%`,
            height: '100%',
            background: 'rgba(22,163,74,0.42)',
          }}
        />
        {/* Yellow high zone (2.5% → ρ_max) */}
        <div
          className="absolute top-0"
          style={{
            left: `${pos(rhoRecPct)}%`,
            width: `${pos(rhoMaxNormPct) - pos(rhoRecPct)}%`,
            height: '100%',
            background: 'rgba(245,158,11,0.30)',
          }}
        />
        {/* Red over-reinforced zone (ρ_max → end) */}
        <div
          className="absolute top-0"
          style={{
            left: `${pos(rhoMaxNormPct)}%`,
            width: `${100 - pos(rhoMaxNormPct)}%`,
            height: '100%',
            background: 'rgba(220,38,38,0.32)',
          }}
        />
        {/* Tick marks for ρ_min, 2.5%, ρ_max */}
        {[rhoMinPct, rhoRecPct, rhoMaxNormPct].map((p, i) => (
          <div
            key={i}
            className="absolute top-0"
            style={{
              left: `${pos(p)}%`,
              height: '100%',
              width: 1,
              background: 'var(--text-secondary)',
              opacity: 0.5,
            }}
          />
        ))}
        {/* Current ρ marker */}
        <div
          className="absolute top-[-2px]"
          style={{
            left: `calc(${pos(rhoPct)}% - 1.5px)`,
            height: BAR_H + 4,
            width: 3,
            background: statusColor,
            boxShadow: `0 0 4px ${statusColor}`,
          }}
        />
      </div>
      <div className="mt-0.5 flex justify-between text-[8.5px]" style={{ color: 'var(--text-muted)' }}>
        <span>ρ_min<br/>{rhoMinPct.toFixed(2)}%</span>
        <span style={{ textAlign: 'center' }}>2.5%<br/>rec.</span>
        <span style={{ textAlign: 'right' }}>ρ_max<br/>{rhoMaxNormPct.toFixed(1)}%</span>
      </div>
      <div className="mt-1 text-[9.5px]" style={{ color: statusColor, lineHeight: 1.3 }}>
        {statusText}
      </div>
    </div>
  );
}

/* =========================================================================
 *  DIAGRAM CARDS
 * ========================================================================= */

/* =========================================================================
 *  BEAM CROSS-SECTION CONTEXT (slab / walls / foundation per type)
 *  Drawn behind the beam concrete to differentiate the typology visually.
 * ========================================================================= */

function BeamSectionContext({
  beamType,
  b,
  h,
  sx,
  sy,
  scale,
  W,
  H,
}: {
  beamType: BeamType;
  b: number;
  h: number;
  sx: (x: number) => number;
  sy: (y: number) => number;
  scale: number;
  W: number;
  H: number;
}) {
  const tSlab = 20;  // 20 cm slab depth
  const tSlabPx = tSlab * scale;
  // Side extension of the slab (beyond the beam) — generous so the slab
  // "feels" continuous on each side of the beam:
  const sidePx = Math.min(140, (W / 2) - (b / 2) * scale - 8);

  const xLeftBeam = sx(-b / 2);
  const xRightBeam = sx(+b / 2);
  const yTopBeam = sy(+h / 2);
  const yBotBeam = sy(-h / 2);

  switch (beamType) {
    case 'rectangular':
    case 'peraltada':
      // Slab on top of the beam (T-section relationship)
      return (
        <g>
          {/* left slab wing */}
          <rect
            x={xLeftBeam - sidePx} y={yTopBeam - tSlabPx}
            width={sidePx} height={tSlabPx}
            fill="#475569" opacity="0.55" stroke="#64748B" strokeWidth="0.7"
          />
          {/* right slab wing */}
          <rect
            x={xRightBeam} y={yTopBeam - tSlabPx}
            width={sidePx} height={tSlabPx}
            fill="#475569" opacity="0.55" stroke="#64748B" strokeWidth="0.7"
          />
        </g>
      );
    case 'invertida':
      // Slab BELOW the beam
      return (
        <g>
          <rect
            x={xLeftBeam - sidePx} y={yBotBeam}
            width={sidePx} height={tSlabPx}
            fill="#475569" opacity="0.55" stroke="#64748B" strokeWidth="0.7"
          />
          <rect
            x={xRightBeam} y={yBotBeam}
            width={sidePx} height={tSlabPx}
            fill="#475569" opacity="0.55" stroke="#64748B" strokeWidth="0.7"
          />
        </g>
      );
    case 'chata':
      // Slab on both sides at the SAME level as the beam
      return (
        <g>
          <rect
            x={xLeftBeam - sidePx} y={yTopBeam}
            width={sidePx} height={h * scale}
            fill="#475569" opacity="0.45" stroke="#64748B" strokeWidth="0.7"
          />
          <rect
            x={xRightBeam} y={yTopBeam}
            width={sidePx} height={h * scale}
            fill="#475569" opacity="0.45" stroke="#64748B" strokeWidth="0.7"
          />
        </g>
      );
    case 'solera': {
      // Brick wall BELOW the beam (the beam sits on top of the wall)
      const wallH = Math.min(H - yBotBeam - 20, h * scale * 3);
      return (
        <rect
          x={xLeftBeam} y={yBotBeam}
          width={(b * scale)} height={wallH}
          fill="url(#brick-beam)" stroke="#451A03" strokeWidth="0.6"
        />
      );
    }
    case 'confinamiento': {
      // Brick walls ABOVE and BELOW the beam (sandwich)
      const wallH = Math.min(80, h * scale * 2);
      return (
        <g>
          <rect
            x={xLeftBeam} y={yTopBeam - wallH}
            width={(b * scale)} height={wallH}
            fill="url(#brick-beam)" stroke="#451A03" strokeWidth="0.6"
          />
          <rect
            x={xLeftBeam} y={yBotBeam}
            width={(b * scale)} height={wallH}
            fill="url(#brick-beam)" stroke="#451A03" strokeWidth="0.6"
          />
        </g>
      );
    }
    case 'borde':
      // Slab ONLY on one side (the interior side; left in our convention)
      return (
        <rect
          x={xLeftBeam - sidePx} y={yTopBeam - tSlabPx}
          width={sidePx} height={tSlabPx}
          fill="#475569" opacity="0.55" stroke="#64748B" strokeWidth="0.7"
        />
      );
    case 'arriostre':
      // No slab / no walls in cross-section. Show ground line (dashed).
      return (
        <g>
          <line
            x1={xLeftBeam - 50} y1={yBotBeam + 8}
            x2={xRightBeam + 50} y2={yBotBeam + 8}
            stroke="#94A3B8" strokeWidth="0.7" strokeDasharray="4 4" opacity="0.6"
          />
          <text x={(xLeftBeam + xRightBeam) / 2} y={yBotBeam + 22}
            textAnchor="middle" fontSize="9" fontFamily="monospace"
            fill="var(--text-muted)" opacity="0.7">
            nivel de cimentación
          </text>
        </g>
      );
    default:
      return null;
  }
}

/* =========================================================================
 *  BEAM TYPE PICKER  +  TYPE METADATA
 * ========================================================================= */

interface BeamTypeMeta {
  id: BeamType;
  label: string;
  title: string;
  description: string;
  note?: string;
}

const BEAM_TYPE_LIST: BeamTypeMeta[] = [
  {
    id: 'rectangular',
    label: 'Rect',
    title: 'Viga rectangular',
    description: 'Viga clásica de pórtico (b × h). Trabaja por flexión + cortante con bloque equivalente de Whitney.',
  },
  {
    id: 'peraltada',
    label: 'Peraltada',
    title: 'Viga peraltada (deep beam)',
    description: 'Cuando h/L ≥ 1/4. La hipótesis de Bernoulli no se cumple: zonas de tracción/compresión se concentran.',
    note: 'ACI 318 cap. 23 sugiere análisis por strut-and-tie. La verificación normativa aquí usa Whitney; revisar manualmente con S-T.',
  },
  {
    id: 'chata',
    label: 'Chata',
    title: 'Viga chata (b ≥ h)',
    description: 'Misma altura que la losa (no cuelga). Ancho mayor o igual al peralte.',
    note: 'E.060 21.5.2.5: en zona sísmica usar estribos confinados en toda la luz cuando b/h es grande.',
  },
  {
    id: 'solera',
    label: 'Solera',
    title: 'Viga solera (albañilería)',
    description: 'Solera de coronación sobre muros de albañilería confinada. Sección pequeña (típico 13×20).',
    note: 'E.070: no requiere diseño sísmico; armadura mínima 4Ø3/8" + estribos Ø1/4@20.',
  },
  {
    id: 'confinamiento',
    label: 'Confinam.',
    title: 'Viga de confinamiento',
    description: 'Viga horizontal sobre muros portantes de albañilería para amarrar y confinar.',
    note: 'E.070 cap. 8 — sección típica 15×20, dimensiones del muro determinan b.',
  },
  {
    id: 'invertida',
    label: 'Invertida',
    title: 'Viga invertida',
    description: 'Cuelga del piso superior (la losa va por debajo). Tracción positiva ocurre arriba, no abajo.',
    note: 'Visualmente las barras se ven espejadas: el armado de tracción está en la parte superior y el de compresión abajo.',
  },
  {
    id: 'arriostre',
    label: 'Arriostre',
    title: 'Viga de arriostre',
    description: 'Conecta cimentaciones o columnas para evitar desplazamientos relativos. Sección reducida.',
    note: 'Habitualmente As_min controla. Su rol es de tracción/compresión axial más que de flexión.',
  },
  {
    id: 'borde',
    label: 'Borde',
    title: 'Viga de borde',
    description: 'Perimetral de la losa. Recibe carga sólo por un lado, lo que puede generar torsión.',
    note: 'Verificar torsión si la losa apoya en un sólo lado (no implementado en esta versión).',
  },
];

const BEAM_TYPE_INFO: Record<BeamType, BeamTypeMeta> = Object.fromEntries(
  BEAM_TYPE_LIST.map((m) => [m.id, m]),
) as Record<BeamType, BeamTypeMeta>;

function BeamTypePicker({ value, onChange }: { value: BeamType; onChange: (t: BeamType) => void }) {
  // 8 types — render in 2 rows of 4 buttons each
  return (
    <div className="grid grid-cols-4 gap-1">
      {BEAM_TYPE_LIST.map((t) => {
        const active = t.id === value;
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => onChange(t.id)}
            className="rounded-[var(--radius-sm)] px-1 py-1.5 text-[9.5px] font-medium transition-colors"
            style={{
              background: active ? 'var(--accent)' : 'var(--surface-2)',
              color: active ? 'var(--surface-base)' : 'var(--text-secondary)',
              border: '1px solid',
              borderColor: active ? 'var(--accent)' : 'var(--border-default)',
            }}
            title={t.title}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

/* =========================================================================
 *  SIMPLE BEAM STATICS — reactions and moment diagram from point loads
 *  (assumes a simply-supported beam of span L with point loads only)
 * ========================================================================= */

function simpleBeamStatics(
  loads: PointLoad[],
  L: number,
): {
  RA: number;
  RB: number;
  Mmax: number;
  xMmax: number;
  /** Returns the moment at x (kgf·cm). Useful for plotting. */
  Mat: (x: number) => number;
  /** Shear at x (kgf). */
  Vat: (x: number) => number;
} {
  if (loads.length === 0 || L <= 0) {
    return { RA: 0, RB: 0, Mmax: 0, xMmax: 0, Mat: () => 0, Vat: () => 0 };
  }
  // Total downward load and reactions
  let RA = 0;
  let RB = 0;
  for (const load of loads) {
    const x = Math.max(0, Math.min(L, load.x_cm));
    RA += load.P_kgf * (L - x) / L;
    RB += load.P_kgf * x / L;
  }
  // Shear at position x (kgf): V(x) = RA - sum(P_i for x_i ≤ x)
  const Vat = (x: number) => {
    let V = RA;
    for (const load of loads) {
      if (load.x_cm <= x) V -= load.P_kgf;
    }
    return V;
  };
  // Moment at position x (kgf·cm): M(x) = RA·x - sum(P_i · (x - x_i) for x_i ≤ x)
  const Mat = (x: number) => {
    let M = RA * x;
    for (const load of loads) {
      if (load.x_cm <= x) M -= load.P_kgf * (x - load.x_cm);
    }
    return M;
  };
  // Locate max moment — it occurs at one of the load positions (shear changes
  // sign at each load) or at the support reactions. Sample at all load
  // positions + a fine grid as a safety net.
  let Mmax = 0;
  let xMmax = 0;
  const candidates = [0, L, ...loads.map((l) => l.x_cm)];
  // Add a uniform fine grid for safety
  const Ngrid = 100;
  for (let i = 0; i <= Ngrid; i++) candidates.push((i / Ngrid) * L);
  for (const x of candidates) {
    const M = Mat(x);
    if (M > Mmax) {
      Mmax = M;
      xMmax = x;
    }
  }
  return { RA, RB, Mmax, xMmax, Mat, Vat };
}

const DIAM_INCH_TO_CM: Record<string, number> = {
  '3/8': 0.9525,
  '1/2': 1.27,
  '5/8': 1.5875,
  '3/4': 1.905,
  '1': 2.54,
  '1 3/8': 3.4925,
};

function diamInchToCm(d: string): number {
  return DIAM_INCH_TO_CM[d] ?? 1.27;
}

function DiagramCard({
  title,
  children,
  toolbar,
}: {
  title: string;
  children: React.ReactNode;
  toolbar?: React.ReactNode;
}) {
  return (
    <div
      className="rounded-[var(--radius-md)] border p-3"
      style={{ background: 'var(--surface-1)', borderColor: 'var(--border-default)' }}
    >
      <div className="mb-2 flex items-center justify-between gap-3">
        <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>
          {title}
        </div>
        {toolbar}
      </div>
      <div className="flex justify-center">{children}</div>
    </div>
  );
}

/* =========================================================================
 *  BEAM SECTION (cross-section) DIAGRAM
 * ========================================================================= */

function BeamSectionDiagram({
  b,
  h,
  cover,
  barsTopTension,
  barsTopCompression,
  barsBotTension,
  barsBotCompression,
  beamType = 'rectangular',
}: {
  b: number;
  h: number;
  cover: number;
  barsTopTension: { n_bars: number; diameters: string[] };
  barsTopCompression?: { n_bars: number; diameters: string[] } | null;
  barsBotTension: { n_bars: number; diameters: string[] };
  barsBotCompression?: { n_bars: number; diameters: string[] } | null;
  beamType?: BeamType;
}) {
  // The TOP layer of the section must accommodate:
  //   (1) tension steel for negative moment (always present)
  //   (2) compression steel for positive moment (only if doubly-reinforced)
  // We display both — when they coexist, the compression bars are drawn in
  // a second inner row slightly below the tension row. Same for bottom.
  const W = 380;
  const H = 380;
  const PAD = 60;
  const scale = Math.min((W - 2 * PAD) / b, (H - 2 * PAD) / h);
  const cx = W / 2;
  const cy = H / 2;
  const sx = (x: number) => cx + x * scale;
  const sy = (y: number) => cy - y * scale;

  const offEdge = cover + 1 + 1;     // ~ cover + d_stirrup + d_bar/2
  const offRow2 = offEdge + 4;       // second row offset (4 cm inboard)

  const yTopT = h / 2 - offEdge;      // top tension row (NEG moment)
  const yTopC = h / 2 - offRow2;      // top compression row (POS moment, doubly)
  const yBotT = -h / 2 + offEdge;     // bot tension row (POS moment)
  const yBotC = -h / 2 + offRow2;     // bot compression row (NEG moment, doubly)

  const xBars = (n: number) =>
    Array.from({ length: n }, (_, i) =>
      n === 1 ? 0 : -b / 2 + offEdge + (i * (b - 2 * offEdge)) / (n - 1),
    );

  const TENSION_COLOR = 'var(--accent)';        // orange — main tension steel
  const COMPRESSION_COLOR = 'var(--brand-violet)'; // violet — compression steel

  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} xmlns="http://www.w3.org/2000/svg">
      <defs>
        <pattern id="hatch-beam" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
          <line x1="0" y1="0" x2="0" y2="6" stroke="var(--text-muted)" strokeOpacity="0.2" strokeWidth="1" />
        </pattern>
        <pattern id="brick-beam" patternUnits="userSpaceOnUse" width="14" height="8">
          <rect width="14" height="8" fill="#7C2D12" opacity="0.55" />
          <line x1="0" y1="0" x2="14" y2="0" stroke="#451A03" strokeWidth="0.6" />
          <line x1="0" y1="4" x2="14" y2="4" stroke="#451A03" strokeWidth="0.6" />
          <line x1="7" y1="0" x2="7" y2="4" stroke="#451A03" strokeWidth="0.6" />
          <line x1="0" y1="4" x2="0" y2="8" stroke="#451A03" strokeWidth="0.6" />
          <line x1="14" y1="4" x2="14" y2="8" stroke="#451A03" strokeWidth="0.6" />
        </pattern>
      </defs>

      {/* CONTEXT — slab / walls / foundation per beam type, drawn behind the beam */}
      <BeamSectionContext beamType={beamType} b={b} h={h} sx={sx} sy={sy} scale={scale} W={W} H={H} />

      {/* concrete */}
      <rect
        x={sx(-b / 2)}
        y={sy(h / 2)}
        width={b * scale}
        height={h * scale}
        fill="url(#hatch-beam)"
        stroke="var(--concrete-outline)"
        strokeWidth="1.6"
      />

      {/* stirrup (rectangular inset) */}
      <rect
        x={sx(-b / 2 + cover + 0.5)}
        y={sy(h / 2 - cover - 0.5)}
        width={(b - 2 * (cover + 0.5)) * scale}
        height={(h - 2 * (cover + 0.5)) * scale}
        fill="none"
        stroke="var(--rebar-stirrup)"
        strokeWidth="1.2"
      />

      {/* TOP layer: tension bars (negative moment) */}
      {xBars(barsTopTension.n_bars).map((x, i) => (
        <circle key={`t-T${i}`} cx={sx(x)} cy={sy(yTopT)} r={5} fill={TENSION_COLOR} stroke="var(--concrete-outline)" strokeWidth="0.6" />
      ))}
      {/* TOP layer: compression bars (from doubly-reinforced positive moment) */}
      {barsTopCompression && barsTopCompression.n_bars > 0 && xBars(barsTopCompression.n_bars).map((x, i) => (
        <circle key={`t-C${i}`} cx={sx(x)} cy={sy(yTopC)} r={4} fill={COMPRESSION_COLOR} stroke="var(--concrete-outline)" strokeWidth="0.6" />
      ))}

      {/* BOT layer: tension bars (positive moment) */}
      {xBars(barsBotTension.n_bars).map((x, i) => (
        <circle key={`b-T${i}`} cx={sx(x)} cy={sy(yBotT)} r={5} fill={TENSION_COLOR} stroke="var(--concrete-outline)" strokeWidth="0.6" />
      ))}
      {/* BOT layer: compression bars (from doubly-reinforced negative moment) */}
      {barsBotCompression && barsBotCompression.n_bars > 0 && xBars(barsBotCompression.n_bars).map((x, i) => (
        <circle key={`b-C${i}`} cx={sx(x)} cy={sy(yBotC)} r={4} fill={COMPRESSION_COLOR} stroke="var(--concrete-outline)" strokeWidth="0.6" />
      ))}

      {/* dimensions */}
      <text x={cx} y={sy(-h / 2) + 22} textAnchor="middle" fontSize="11" fill="var(--text-secondary)" fontFamily="monospace">
        b = {b} cm
      </text>
      <text
        x={sx(-b / 2) - 18}
        y={cy}
        textAnchor="middle"
        fontSize="11"
        fill="var(--text-secondary)"
        fontFamily="monospace"
        transform={`rotate(-90, ${sx(-b / 2) - 18}, ${cy})`}
      >
        h = {h} cm
      </text>

      {/* compact legend at the bottom */}
      <g transform={`translate(8, ${H - 8})`}>
        <circle cx="4" cy="0" r="3.5" fill={TENSION_COLOR} />
        <text x="12" y="3" fontSize="9" fill="var(--text-muted)" fontFamily="var(--font-body)">tracción</text>
        {(barsTopCompression || barsBotCompression) && (
          <>
            <circle cx="72" cy="0" r="3" fill={COMPRESSION_COLOR} />
            <text x="80" y="3" fontSize="9" fill="var(--text-muted)" fontFamily="var(--font-body)">compresión (2× armado)</text>
          </>
        )}
      </g>
    </svg>
  );
}

/* =========================================================================
 *  BEAM ELEVATION (longitudinal) DIAGRAM
 * ========================================================================= */

/* =========================================================================
 *  MOMENT ENVELOPE DIAGRAM  M(x)  —  demand (parabolic) vs capacity (±φMn)
 * ========================================================================= */

function MomentEnvelopeDiagram({
  L,
  Mu_neg_left,
  Mu_pos_mid,
  Mu_neg_right,
  phi_Mn_pos,
  phi_Mn_neg_left,
  phi_Mn_neg_right,
  h,
}: {
  L: number;
  Mu_neg_left: number;
  Mu_pos_mid: number;
  Mu_neg_right: number;
  phi_Mn_pos: number;
  phi_Mn_neg_left: number;
  phi_Mn_neg_right: number;
  h: number;
}) {
  // Parabolic demand interpolation through (0, -Mu_neg_left), (L/2, +Mu_pos_mid),
  // (L, -Mu_neg_right). Sign convention: + = positive moment (tension below),
  // − = negative moment (tension above).
  const m0 = -Mu_neg_left;
  const mMid = +Mu_pos_mid;
  const mL = -Mu_neg_right;
  // M(x) = a·x² + b·x + c
  const c = m0;
  const a = (2 * (mL + m0 - 2 * mMid)) / (L * L);
  const b = (4 * mMid - 3 * m0 - mL) / L;
  const M = (x: number) => a * x * x + b * x + c;

  // Capacity envelope. Bottom bars are present along the full span → +φMn_pos.
  // Top bars are typically cut at ~0.25L from each support (cutoff length =
  // L/4 from face). We approximate: φMn_neg is at the supports and reduces
  // to 0 within the central 50% of the span.
  const phiMnNegEnv = (x: number) => {
    const xL_cut = L * 0.25;
    const xR_cut = L * 0.75;
    if (x <= xL_cut) return -phi_Mn_neg_left;
    if (x >= xR_cut) return -phi_Mn_neg_right;
    return 0;
  };

  // Plot bounds
  const W = 880;
  const H_svg = 320;
  const PAD_L = 60;
  const PAD_R = 30;
  const PAD_T = 30;
  const PAD_B = 50;
  const plotW = W - PAD_L - PAD_R;
  const plotH = H_svg - PAD_T - PAD_B;

  // Y axis range — symmetric (positive up, negative down)
  const mMax = Math.max(
    Math.abs(phi_Mn_pos),
    Math.abs(phi_Mn_neg_left),
    Math.abs(phi_Mn_neg_right),
    Math.abs(m0), Math.abs(mMid), Math.abs(mL),
  );
  const yMax = mMax * 1.15;  // headroom

  const sx = (x: number) => PAD_L + (x / L) * plotW;
  const sy = (M_val: number) => PAD_T + plotH / 2 - (M_val / yMax) * (plotH / 2);

  // Build demand path (parabola sampled at N points)
  const N = 60;
  const demandPts: string[] = [];
  for (let i = 0; i <= N; i++) {
    const x = (i / N) * L;
    demandPts.push(`${sx(x).toFixed(1)},${sy(M(x)).toFixed(1)}`);
  }
  const demandPath = 'M ' + demandPts.join(' L ');

  // Capacity envelope (top + bottom)
  const yPhiPos = sy(+phi_Mn_pos);
  const yPhiNegL = sy(-phi_Mn_neg_left);
  const yPhiNegR = sy(-phi_Mn_neg_right);
  const xCutL = sx(L * 0.25);
  const xCutR = sx(L * 0.75);
  const yZero = sy(0);

  // Check at critical points (left support, midspan, right support)
  const checks = [
    { x: 0, M_d: m0, M_c: -phi_Mn_neg_left, label: 'Apoyo izq.' },
    { x: L / 2, M_d: mMid, M_c: +phi_Mn_pos, label: 'Centro' },
    { x: L, M_d: mL, M_c: -phi_Mn_neg_right, label: 'Apoyo der.' },
  ];

  return (
    <svg width={W} height={H_svg} viewBox={`0 0 ${W} ${H_svg}`}>
      {/* Axes */}
      <line x1={PAD_L} y1={PAD_T} x2={PAD_L} y2={PAD_T + plotH} stroke="var(--text-muted)" strokeWidth="0.8" />
      <line x1={PAD_L} y1={yZero} x2={W - PAD_R} y2={yZero} stroke="var(--text-muted)" strokeWidth="0.8" />
      {/* Zero line label */}
      <text x={PAD_L - 6} y={yZero + 3} textAnchor="end" fontSize="9" fontFamily="monospace" fill="var(--text-muted)">0</text>
      {/* Y ticks */}
      {[-1, -0.5, 0.5, 1].map((f, i) => {
        const M_t = f * mMax * 0.9;
        return (
          <g key={`yt-${i}`}>
            <line x1={PAD_L - 4} y1={sy(M_t)} x2={PAD_L} y2={sy(M_t)} stroke="var(--text-muted)" strokeWidth="0.6" />
            <text x={PAD_L - 6} y={sy(M_t) + 3} textAnchor="end" fontSize="8.5" fontFamily="monospace" fill="var(--text-muted)">
              {(M_t / 1e5).toFixed(1)}
            </text>
          </g>
        );
      })}
      {/* X ticks */}
      {[0, 0.25, 0.5, 0.75, 1].map((f) => {
        const x = f * L;
        return (
          <g key={`xt-${f}`}>
            <line x1={sx(x)} y1={yZero - 3} x2={sx(x)} y2={yZero + 3} stroke="var(--text-muted)" strokeWidth="0.6" />
            <text x={sx(x)} y={yZero + 18} textAnchor="middle" fontSize="8.5" fontFamily="monospace" fill="var(--text-muted)">
              {x.toFixed(0)}
            </text>
          </g>
        );
      })}

      {/* CAPACITY: positive (φMn_pos, bottom bars) — horizontal line across the span */}
      <line
        x1={sx(0)} y1={yPhiPos} x2={sx(L)} y2={yPhiPos}
        stroke="rgba(22,163,74,0.9)" strokeWidth="1.8" strokeDasharray="6 3"
      />
      <text x={sx(L) - 6} y={yPhiPos - 4} textAnchor="end" fontSize="9.5" fontFamily="monospace" fill="rgba(22,163,74,0.95)">
        +φMn = {(phi_Mn_pos / 1e5).toFixed(2)} tonf·m
      </text>

      {/* CAPACITY: negative left and right (φMn_neg), piecewise (extend ~L/4 from each support) */}
      <line x1={sx(0)} y1={yPhiNegL} x2={xCutL} y2={yPhiNegL} stroke="rgba(220,38,38,0.9)" strokeWidth="1.8" strokeDasharray="6 3" />
      <line x1={xCutR} y1={yPhiNegR} x2={sx(L)} y2={yPhiNegR} stroke="rgba(220,38,38,0.9)" strokeWidth="1.8" strokeDasharray="6 3" />
      {/* Vertical dashed lines showing approximate top-bar cut-off points */}
      <line x1={xCutL} y1={yPhiNegL} x2={xCutL} y2={yZero} stroke="rgba(220,38,38,0.4)" strokeWidth="0.7" strokeDasharray="3 3" />
      <line x1={xCutR} y1={yPhiNegR} x2={xCutR} y2={yZero} stroke="rgba(220,38,38,0.4)" strokeWidth="0.7" strokeDasharray="3 3" />
      <text x={sx(0) + 6} y={yPhiNegL + 12} fontSize="9.5" fontFamily="monospace" fill="rgba(220,38,38,0.95)">
        −φMn = {(phi_Mn_neg_left / 1e5).toFixed(2)} tonf·m
      </text>

      {/* DEMAND parabola */}
      <path d={demandPath} stroke="var(--accent)" strokeWidth="2.2" fill="none" />

      {/* Critical-point markers */}
      {checks.map((c, i) => {
        const ok = Math.abs(c.M_d) <= Math.abs(c.M_c);
        return (
          <g key={`cp-${i}`}>
            <circle
              cx={sx(c.x)} cy={sy(c.M_d)} r={4}
              fill={ok ? 'var(--success)' : 'var(--danger)'}
              stroke="var(--surface-base)" strokeWidth="1"
            />
            <text x={sx(c.x)} y={sy(c.M_d) + (c.M_d > 0 ? -10 : 18)}
              textAnchor="middle" fontSize="9.5" fontFamily="monospace"
              fill={ok ? 'var(--success)' : 'var(--danger)'}
              style={{ fontWeight: 700 }}
            >
              {(c.M_d / 1e5).toFixed(2)}
            </text>
          </g>
        );
      })}

      {/* Title hints */}
      <text x={W / 2} y={PAD_T - 12} textAnchor="middle" fontSize="11" fontFamily="monospace" fill="var(--text-secondary)">
        Momento M(x) — tonf·m  ·  L = {L} cm
      </text>
      <text x={PAD_L - 35} y={PAD_T + plotH / 2} fontSize="10" fontFamily="monospace" fill="var(--text-muted)"
        transform={`rotate(-90, ${PAD_L - 35}, ${PAD_T + plotH / 2})`}>
        Mu / φMn
      </text>
      <text x={W - PAD_R} y={H_svg - 14} textAnchor="end" fontSize="9" fontFamily="monospace" fill="var(--text-muted)">
        x (cm)
      </text>

      {/* Legend */}
      <g transform={`translate(${PAD_L + 4}, ${H_svg - 14})`}>
        <line x1="0" y1="-2" x2="14" y2="-2" stroke="var(--accent)" strokeWidth="2" />
        <text x="18" y="2" fontSize="9" fontFamily="monospace" fill="var(--text-muted)">Demanda Mu</text>
        <line x1="100" y1="-2" x2="114" y2="-2" stroke="rgba(22,163,74,0.9)" strokeWidth="2" strokeDasharray="4 2" />
        <text x="118" y="2" fontSize="9" fontFamily="monospace" fill="var(--text-muted)">+φMn (positivo)</text>
        <line x1="220" y1="-2" x2="234" y2="-2" stroke="rgba(220,38,38,0.9)" strokeWidth="2" strokeDasharray="4 2" />
        <text x="238" y="2" fontSize="9" fontFamily="monospace" fill="var(--text-muted)">−φMn (negativo, en apoyos)</text>
      </g>
      <text x={W - PAD_R - 4} y={H_svg - 14} textAnchor="end" fontSize="8.5" fontFamily="monospace" fill="var(--text-muted)">
        Mu mostrado en tonf·m, x en cm
      </text>
    </svg>
  );
}

/* =========================================================================
 *  SHEAR ENVELOPE DIAGRAM  V(x)  —  demand (linear) vs capacity φVn per zone
 * ========================================================================= */

function ShearEnvelopeDiagram({
  L,
  Vu_left,
  Vu_right,
  zones,
  Vc,
  Av_one_leg,
  n_legs,
  fy,
  d,
}: {
  L: number;
  Vu_left: number;
  Vu_right: number;
  zones: Array<{ label: string; length: number; spacing: number; n_stirrups: number }>;
  Vc: number;
  Av_one_leg: number;
  n_legs: number;
  fy: number;
  d: number;
}) {
  // Demand: linear from +Vu_left at x=0 to -Vu_right at x=L (passes through 0 somewhere).
  const V = (x: number) => Vu_left - ((Vu_left + Vu_right) / L) * x;

  // Capacity: φVn = φ·(Vc + Vs) per zone, where Vs depends on spacing.
  const PHI_SHEAR = 0.85;
  const Av_total = Av_one_leg * n_legs;
  const phi_Vn_at_zone = (zoneIdx: number) => {
    const s = Math.max(zones[zoneIdx]?.spacing ?? 1, 0.01);
    const Vs = (Av_total * fy * d) / s;
    return PHI_SHEAR * (Vc + Vs);
  };

  // Plot bounds
  const W = 880;
  const H_svg = 280;
  const PAD_L = 60;
  const PAD_R = 30;
  const PAD_T = 30;
  const PAD_B = 50;
  const plotW = W - PAD_L - PAD_R;
  const plotH = H_svg - PAD_T - PAD_B;

  const phiVn_max = Math.max(...zones.map((_, i) => phi_Vn_at_zone(i)));
  const vMax = Math.max(Math.abs(Vu_left), Math.abs(Vu_right), phiVn_max) * 1.15;

  const sx = (x: number) => PAD_L + (x / L) * plotW;
  const sy = (V_val: number) => PAD_T + plotH / 2 - (V_val / vMax) * (plotH / 2);
  const yZero = sy(0);

  // Demand path (linear, just 2 points)
  const demandPath = `M ${sx(0)},${sy(Vu_left)} L ${sx(L)},${sy(-Vu_right)}`;

  // Capacity as step function: per zone, |φVn| line above (positive side) and
  // mirrored below (negative). Demand can be positive or negative depending
  // on which side of the beam.
  let cursor = 0;
  const capBlocks: { x1: number; x2: number; val: number; label: string; spacing: number; n: number }[] = [];
  zones.forEach((z, i) => {
    capBlocks.push({
      x1: cursor,
      x2: cursor + z.length,
      val: phi_Vn_at_zone(i),
      label: z.label,
      spacing: z.spacing,
      n: z.n_stirrups,
    });
    cursor += z.length;
  });

  return (
    <svg width={W} height={H_svg} viewBox={`0 0 ${W} ${H_svg}`}>
      {/* Axes */}
      <line x1={PAD_L} y1={PAD_T} x2={PAD_L} y2={PAD_T + plotH} stroke="var(--text-muted)" strokeWidth="0.8" />
      <line x1={PAD_L} y1={yZero} x2={W - PAD_R} y2={yZero} stroke="var(--text-muted)" strokeWidth="0.8" />
      <text x={PAD_L - 6} y={yZero + 3} textAnchor="end" fontSize="9" fontFamily="monospace" fill="var(--text-muted)">0</text>
      {/* Y ticks */}
      {[-1, -0.5, 0.5, 1].map((f, i) => {
        const V_t = f * vMax * 0.9;
        return (
          <g key={`yt-${i}`}>
            <line x1={PAD_L - 4} y1={sy(V_t)} x2={PAD_L} y2={sy(V_t)} stroke="var(--text-muted)" strokeWidth="0.6" />
            <text x={PAD_L - 6} y={sy(V_t) + 3} textAnchor="end" fontSize="8.5" fontFamily="monospace" fill="var(--text-muted)">
              {(V_t / 1000).toFixed(1)}
            </text>
          </g>
        );
      })}
      {/* X ticks */}
      {[0, 0.25, 0.5, 0.75, 1].map((f) => {
        const x = f * L;
        return (
          <g key={`xt-${f}`}>
            <line x1={sx(x)} y1={yZero - 3} x2={sx(x)} y2={yZero + 3} stroke="var(--text-muted)" strokeWidth="0.6" />
            <text x={sx(x)} y={yZero + 18} textAnchor="middle" fontSize="8.5" fontFamily="monospace" fill="var(--text-muted)">
              {x.toFixed(0)}
            </text>
          </g>
        );
      })}

      {/* CAPACITY blocks per zone (mirrored top + bottom envelopes) */}
      {capBlocks.map((b, i) => {
        const x1 = sx(b.x1);
        const x2 = sx(b.x2);
        const yTop = sy(+b.val);
        const yBot = sy(-b.val);
        const isConf = b.label.toLowerCase().includes('confin');
        const fill = isConf ? 'rgba(245,158,11,0.16)' : 'rgba(139,92,246,0.13)';
        return (
          <g key={`cap-${i}`}>
            {/* Fill between top and bottom envelopes — visualizes the "safe" zone */}
            <rect x={x1} y={yTop} width={x2 - x1} height={yBot - yTop} fill={fill} />
            {/* Top line */}
            <line x1={x1} y1={yTop} x2={x2} y2={yTop} stroke="rgba(22,163,74,0.95)" strokeWidth="1.8" strokeDasharray="6 3" />
            {/* Bottom line */}
            <line x1={x1} y1={yBot} x2={x2} y2={yBot} stroke="rgba(22,163,74,0.95)" strokeWidth="1.8" strokeDasharray="6 3" />
            {/* zone label */}
            <text x={(x1 + x2) / 2} y={H_svg - 26} textAnchor="middle" fontSize="9" fontFamily="monospace" fill="var(--text-muted)">
              {b.label}
            </text>
            <text x={(x1 + x2) / 2} y={H_svg - 14} textAnchor="middle" fontSize="8.5" fontFamily="monospace" fill="var(--text-muted)">
              {b.n}@{b.spacing.toFixed(0)} cm
            </text>
          </g>
        );
      })}

      {/* DEMAND line */}
      <path d={demandPath} stroke="var(--accent)" strokeWidth="2.2" fill="none" />

      {/* Endpoint markers */}
      <circle cx={sx(0)} cy={sy(Vu_left)} r={4} fill="var(--accent)" stroke="var(--surface-base)" strokeWidth="1" />
      <text x={sx(0) + 8} y={sy(Vu_left) - 6} fontSize="9.5" fontFamily="monospace" fill="var(--accent)" style={{ fontWeight: 700 }}>
        {(Vu_left / 1000).toFixed(1)} tonf
      </text>
      <circle cx={sx(L)} cy={sy(-Vu_right)} r={4} fill="var(--accent)" stroke="var(--surface-base)" strokeWidth="1" />
      <text x={sx(L) - 8} y={sy(-Vu_right) + 14} textAnchor="end" fontSize="9.5" fontFamily="monospace" fill="var(--accent)" style={{ fontWeight: 700 }}>
        −{(Vu_right / 1000).toFixed(1)} tonf
      </text>

      {/* Title hints */}
      <text x={W / 2} y={PAD_T - 12} textAnchor="middle" fontSize="11" fontFamily="monospace" fill="var(--text-secondary)">
        Cortante V(x) — tonf  ·  L = {L} cm
      </text>
      <text x={PAD_L - 35} y={PAD_T + plotH / 2} fontSize="10" fontFamily="monospace" fill="var(--text-muted)"
        transform={`rotate(-90, ${PAD_L - 35}, ${PAD_T + plotH / 2})`}>
        Vu / φVn
      </text>
    </svg>
  );
}

function BeamElevationDiagram({
  L,
  h,
  zones,
  posBars,
  negBars,
  inverted = false,
  appliedLoads = [],
  onLoadDrag,
}: {
  L: number;
  h: number;
  zones: Array<{ label: string; length: number; spacing: number; n_stirrups: number }>;
  posBars: string;
  negBars: string;
  /** When true the beam visually hangs from the floor above: supports drawn at
   *  the top of the beam (the slab/floor is BELOW the beam centerline). */
  inverted?: boolean;
  /** Optional list of point loads drawn as down-arrows on the top of the beam. */
  appliedLoads?: PointLoad[];
  /** Callback fired while the user drags an arrow along the beam. The new
   *  position is reported in cm from the left support. */
  onLoadDrag?: (id: string, x_cm: number) => void;
}) {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  // Beam is drawn as a long horizontal rectangle.
  const W = 900;
  const H = 240;
  const PAD_X = 60;
  const PAD_Y = 50;
  const beamHeight = Math.min((H - 2 * PAD_Y), 120);
  const beamWidth = W - 2 * PAD_X;
  const xL = PAD_X;
  const xR = xL + beamWidth;
  const yT = PAD_Y;
  const yB = yT + beamHeight;

  const cmPerPx = L / beamWidth; // ≈ cm per pixel

  // Build the stirrup positions across the full span
  const stirrupTicks = useMemo(() => {
    const ticks: { x: number; zone: string }[] = [];
    let cursor = 0;
    for (const z of zones) {
      let x = cursor + z.spacing / 2;
      while (x < cursor + z.length && ticks.length < 200) {
        ticks.push({ x: x, zone: z.label });
        x += z.spacing;
      }
      cursor += z.length;
    }
    return ticks;
  }, [zones]);

  const toPx = (cm: number) => xL + (cm / cmPerPx);

  // While the user is dragging an arrow, listen for mouse moves on the
  // document and translate the mouse X position to beam x_cm. The math uses
  // the SVG's bounding rect to convert client coords to the viewBox space.
  useEffect(() => {
    if (!dragId || !onLoadDrag) return;
    const svgEl = svgRef.current;
    if (!svgEl) return;

    const handleMove = (e: MouseEvent) => {
      const rect = svgEl.getBoundingClientRect();
      // map screen X → viewBox X (the SVG scales linearly to its container)
      const xViewBox = ((e.clientX - rect.left) / rect.width) * W;
      // viewBox X → cm along the beam
      const xCm = (xViewBox - xL) * cmPerPx;
      const clamped = Math.max(0, Math.min(L, xCm));
      onLoadDrag(dragId, clamped);
    };
    const handleUp = () => setDragId(null);

    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
    document.body.style.cursor = 'grabbing';
    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
      document.body.style.cursor = '';
    };
  }, [dragId, onLoadDrag, L, xL, cmPerPx, W]);

  return (
    <svg ref={svgRef} width={W} height={H} viewBox={`0 0 ${W} ${H}`} xmlns="http://www.w3.org/2000/svg">
      <defs>
        <pattern id="hatch-elev" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
          <line x1="0" y1="0" x2="0" y2="6" stroke="var(--text-muted)" strokeOpacity="0.15" strokeWidth="1" />
        </pattern>
      </defs>

      {/* beam concrete (rectangle = elevation) */}
      <rect
        x={xL}
        y={yT}
        width={beamWidth}
        height={beamHeight}
        fill="url(#hatch-elev)"
        stroke="var(--concrete-outline)"
        strokeWidth="1.4"
      />

      {/* supports — small triangles. For a normal beam they sit BELOW the beam
          (the beam rests on columns/walls). For an inverted beam they sit
          ABOVE the beam (the beam hangs from the floor above). */}
      {inverted ? (
        <>
          <polygon
            points={`${xL},${yT - 1} ${xL - 10},${yT - 18} ${xL + 10},${yT - 18}`}
            fill="var(--text-muted)"
            opacity="0.7"
          />
          <polygon
            points={`${xR},${yT - 1} ${xR - 10},${yT - 18} ${xR + 10},${yT - 18}`}
            fill="var(--text-muted)"
            opacity="0.7"
          />
          {/* hint line representing the floor above */}
          <line
            x1={xL - 14} y1={yT - 18}
            x2={xR + 14} y2={yT - 18}
            stroke="var(--text-muted)" strokeWidth="1" strokeDasharray="3 3" opacity="0.5"
          />
        </>
      ) : (
        <>
          <polygon
            points={`${xL},${yB + 1} ${xL - 10},${yB + 18} ${xL + 10},${yB + 18}`}
            fill="var(--text-muted)"
            opacity="0.7"
          />
          <polygon
            points={`${xR},${yB + 1} ${xR - 10},${yB + 18} ${xR + 10},${yB + 18}`}
            fill="var(--text-muted)"
            opacity="0.7"
          />
        </>
      )}

      {/* zone background bands */}
      {(() => {
        let cursor = 0;
        return zones.map((z, i) => {
          const x1 = toPx(cursor);
          const x2 = toPx(cursor + z.length);
          cursor += z.length;
          const isConfin = z.label.toLowerCase().includes('confin');
          return (
            <rect
              key={i}
              x={x1}
              y={yT}
              width={x2 - x1}
              height={beamHeight}
              fill={isConfin ? 'var(--accent)' : 'var(--brand-violet)'}
              opacity="0.08"
            />
          );
        });
      })()}

      {/* top longitudinal bars (drawn near top of beam) */}
      <line
        x1={xL + 4}
        y1={yT + 8}
        x2={xR - 4}
        y2={yT + 8}
        stroke="var(--accent)"
        strokeWidth="2.6"
      />
      {/* bottom longitudinal bars */}
      <line
        x1={xL + 4}
        y1={yB - 8}
        x2={xR - 4}
        y2={yB - 8}
        stroke="var(--accent)"
        strokeWidth="2.6"
      />

      {/* stirrups */}
      {stirrupTicks.map((t, i) => (
        <line
          key={i}
          x1={toPx(t.x)}
          y1={yT + 3}
          x2={toPx(t.x)}
          y2={yB - 3}
          stroke="var(--rebar-stirrup)"
          strokeWidth="1"
          opacity="0.85"
        />
      ))}

      {/* APPLIED POINT LOADS — down-pointing arrows above the beam. Each
          arrow is interactive: click and DRAG horizontally to slide the load
          along the beam axis (releases mouse → updates store, which
          re-runs the analysis with the new position). */}
      {appliedLoads.map((load) => {
        const x = toPx(load.x_cm);
        const arrowH = 28;
        const headSize = 7;
        const arrowTopY = yT - arrowH - 6;
        const arrowTipY = yT - 6;
        const P_tonf = load.P_kgf / 1000;
        const isDragging = dragId === load.id;
        const groupCursor = onLoadDrag ? (isDragging ? 'grabbing' : 'grab') : 'default';
        return (
          <g
            key={`load-${load.id}`}
            style={{ cursor: groupCursor }}
            onMouseDown={(e) => {
              if (!onLoadDrag) return;
              e.preventDefault();
              setDragId(load.id);
            }}
          >
            {/* Larger invisible hit target around the arrow for easier grabbing */}
            <rect
              x={x - 30}
              y={arrowTopY - 20}
              width={60}
              height={arrowH + 28}
              fill="transparent"
            />
            {/* Shaft */}
            <line
              x1={x} y1={arrowTopY}
              x2={x} y2={arrowTipY}
              stroke="var(--accent)"
              strokeWidth={isDragging ? '3' : '2'}
            />
            {/* Arrowhead */}
            <polygon
              points={`${x},${arrowTipY + 2} ${x - headSize},${arrowTipY - headSize - 1} ${x + headSize},${arrowTipY - headSize - 1}`}
              fill="var(--accent)"
            />
            {/* Magnitude label (label + x position when dragging) */}
            <rect
              x={x - 36} y={arrowTopY - 16}
              width="72" height="14"
              rx="3"
              fill="rgba(15,23,42,0.92)"
              stroke="var(--accent)"
              strokeWidth={isDragging ? '1.2' : '0.6'}
            />
            <text
              x={x} y={arrowTopY - 6}
              textAnchor="middle"
              fontSize="9.5"
              fontFamily="monospace"
              fill="var(--accent)"
              style={{ fontWeight: 700, userSelect: 'none' }}
            >
              {P_tonf >= 1 ? `${P_tonf.toFixed(2)} tf` : `${load.P_kgf.toFixed(0)} kgf`}
            </text>
            {/* When dragging, show the current x position in cm under the arrow tip */}
            {isDragging && (
              <text
                x={x} y={yT - 2 + 14}
                textAnchor="middle"
                fontSize="9"
                fontFamily="monospace"
                fill="var(--accent)"
                style={{ fontWeight: 700, userSelect: 'none' }}
              >
                x = {load.x_cm.toFixed(0)} cm
              </text>
            )}
          </g>
        );
      })}

      {/* zone labels under the beam */}
      {(() => {
        let cursor = 0;
        return zones.map((z, i) => {
          const x1 = toPx(cursor);
          const x2 = toPx(cursor + z.length);
          cursor += z.length;
          const mid = (x1 + x2) / 2;
          return (
            <g key={`label-${i}`}>
              <line x1={x1} y1={yB + 26} x2={x2} y2={yB + 26} stroke="var(--dim-line)" strokeWidth="0.6" />
              <line x1={x1} y1={yB + 22} x2={x1} y2={yB + 30} stroke="var(--dim-line)" strokeWidth="0.6" />
              <line x1={x2} y1={yB + 22} x2={x2} y2={yB + 30} stroke="var(--dim-line)" strokeWidth="0.6" />
              <text x={mid} y={yB + 41} textAnchor="middle" fontSize="10" fill="var(--text-secondary)" fontFamily="monospace">
                {z.label}
              </text>
              <text x={mid} y={yB + 55} textAnchor="middle" fontSize="9" fill="var(--text-muted)" fontFamily="monospace">
                {z.n_stirrups}@{z.spacing.toFixed(0)} cm
              </text>
            </g>
          );
        });
      })()}

      {/* total length label on top */}
      <text x={W / 2} y={20} textAnchor="middle" fontSize="11" fill="var(--text-secondary)" fontFamily="monospace">
        L = {L} cm    ·    h = {h} cm    ·    Sup: {negBars}    ·    Inf: {posBars}
      </text>
    </svg>
  );
}
